// Additional reports for ReportsPanel: admissions enquiries, follow-ups,
// director's one-page summary, and UDISE+ enrolment by class/gender/category.
import { useEffect, useState } from 'react';
import { Printer, Download, AlertTriangle } from 'lucide-react';
import { getEnquiryReport, getFollowupReport, getDirectorReport, getUdiseReport } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader, StatTile } from '../ui/Card';
import PrintSheet, { PrintHeader } from '../ui/PrintSheet';
import { Msg, errText, inputCls, theadRowCls, EmptyRow } from '../ui/Form';
import { inr, fmtDate, todayISO, downloadCsv } from '../ui/format';

function useLoad(fn, deps) {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState('');
  useEffect(() => { setMsg(''); fn().then(setData).catch(err => setMsg(errText(err))); }, deps); // eslint-disable-line
  return [data, msg];
}

const pctBar = (pct) => (
  <div className="h-1.5 bg-line-soft rounded-full overflow-hidden w-24 inline-block align-middle ml-2"><div className="h-full bg-transit" style={{ width: `${Math.min(100, pct || 0)}%` }} /></div>
);

// ── Enquiries ───────────────────────────────────────────────
export function EnquiryReport() {
  const [from, setFrom] = useState(() => `${todayISO().slice(0, 4)}-01-01`);
  const [to, setTo] = useState(todayISO);
  const [data, msg] = useLoad(() => getEnquiryReport({ from, to }), [from, to]);
  return (
    <>
      <Card className="p-4 mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">From<input type="date" className={inputCls} value={from} max={to} onChange={e => setFrom(e.target.value)} /></label>
        <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">To<input type="date" className={inputCls} value={to} min={from} onChange={e => setTo(e.target.value)} /></label>
      </Card>
      <Msg msg={msg} className="mb-3" />
      {data && (
        <>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3.5 mb-4">
            <StatTile tone="transit" label="Enquiries" value={data.total} />
            <StatTile tone="success" label="Admitted" value={data.by_status.admitted} caption={`${data.conversion_percent}% conversion`} />
            <StatTile tone="clearance" label="In progress" value={data.by_status.new + data.by_status.called + data.by_status.visited} />
            <StatTile tone="slate" label="Closed / lost" value={data.by_status.closed} />
          </div>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4">
            <BreakdownTable title="By source" keyLabel="Source" rows={data.by_source.map(r => ({ key: r.source, n: r.n, admitted: r.admitted }))} />
            <BreakdownTable title="By class sought" keyLabel="Class" rows={data.by_class.map(r => ({ key: r.class_sought, n: r.n, admitted: r.admitted }))} />
          </div>
        </>
      )}
    </>
  );
}

function BreakdownTable({ title, keyLabel, rows }) {
  return (
    <Card>
      <CardHeader title={title} />
      <table className="w-full text-sm">
        <thead><tr className={theadRowCls}><th className="px-4 py-2.5">{keyLabel}</th><th className="px-4 py-2.5 text-right">Enquiries</th><th className="px-4 py-2.5 text-right">Admitted</th><th className="px-4 py-2.5">Conversion</th></tr></thead>
        <tbody>
          {rows.map(r => {
            const pct = r.n ? Math.round((r.admitted / r.n) * 100) : 0;
            return (
              <tr key={r.key} className="border-t border-line-soft">
                <td className="px-4 py-2.5 font-semibold">{r.key}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{r.n}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{r.admitted}</td>
                <td className="px-4 py-2.5 tabular-nums">{pct}%{pctBar(pct)}</td>
              </tr>
            );
          })}
          {!rows.length && <EmptyRow cols={4}>No enquiries in this period</EmptyRow>}
        </tbody>
      </table>
    </Card>
  );
}

// ── Follow-ups ──────────────────────────────────────────────
export function FollowupReport() {
  const [data, msg] = useLoad(() => getFollowupReport(), []);
  return (
    <>
      <Msg msg={msg} className="mb-3" />
      {data && (
        <Card>
          <CardHeader title={`${data.length} open enquir${data.length === 1 ? 'y' : 'ies'} · ${data.filter(r => r.overdue).length} overdue`} action={
            <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" disabled={!data.length}
              onClick={() => downloadCsv('followups.csv', [
                { key: 'student_name', label: 'Student' }, { key: 'guardian_name', label: 'Parent' }, { key: 'phone', label: 'Phone' },
                { key: 'class_sought', label: 'Class' }, { key: 'status', label: 'Status' }, { key: 'follow_up_date', label: 'Follow-up' },
                { key: 'calls', label: 'Calls' }, { key: 'last_note', label: 'Last note' },
              ], data)}><Download size={13} /> CSV</Button>
          } />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Student</th><th className="px-4 py-2.5">Phone</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Follow-up</th><th className="px-4 py-2.5">Calls</th><th className="px-4 py-2.5">Last note</th></tr></thead>
              <tbody>
                {data.map(r => (
                  <tr key={r.id} className="border-t border-line-soft">
                    <td className="px-4 py-2.5 font-semibold">{r.student_name}<span className="block text-xs text-ink-soft font-normal">{r.guardian_name || ''}{r.class_sought ? ` · Class ${r.class_sought}` : ''}</span></td>
                    <td className="px-4 py-2.5"><a href={`tel:${r.phone}`} className="text-transit-deep font-semibold">{r.phone}</a></td>
                    <td className="px-4 py-2.5 capitalize">{r.status}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{r.follow_up_date ? fmtDate(r.follow_up_date) : '—'}{r.overdue && <span className="block"><Badge tone="critical">Overdue</Badge></span>}</td>
                    <td className="px-4 py-2.5 tabular-nums">{r.calls}</td>
                    <td className="px-4 py-2.5 text-ink-soft max-w-[280px]">{r.last_note || '—'}</td>
                  </tr>
                ))}
                {!data.length && <EmptyRow cols={6}>No open enquiries</EmptyRow>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

// ── Director summary ────────────────────────────────────────
export function DirectorReport({ schoolName }) {
  const [data, msg] = useLoad(() => getDirectorReport(), []);
  const [print, setPrint] = useState(false);
  if (msg) return <Msg msg={msg} />;
  if (!data) return <p className="text-sm text-ink-soft">Loading…</p>;
  const body = <DirectorBody data={data} />;
  return (
    <>
      <div className="flex justify-end mb-3"><Button variant="secondary" onClick={() => setPrint(true)} className="flex items-center gap-1.5"><Printer size={14} /> Print</Button></div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-3.5 mb-4">
        <StatTile tone="transit" label="Students" value={data.totals.strength} caption={`${data.totals.boys} boy${data.totals.boys === 1 ? '' : 's'} · ${data.totals.girls} girl${data.totals.girls === 1 ? '' : 's'}`} />
        <StatTile tone="success" label="Present today" value={data.totals.present_today} caption={data.attendance_month_percent != null ? `${data.attendance_month_percent}% this month` : 'No attendance yet'} />
        <StatTile tone="success" label="Fees collected (month)" value={inr(data.fees.collected_month)} caption={`This month's bill ${inr(data.fees.billed_month)} · includes arrears collected`} />
        <StatTile tone="caution" label="Outstanding dues" value={inr(data.fees.outstanding)} />
        <StatTile tone="critical" label="Expenses (month)" value={inr(data.expenses_month)} />
        <StatTile tone="clearance" label="Admissions (month)" value={data.admissions.admitted_this_month} caption={`${data.admissions.this_month} new enquiries · ${data.admissions.open} open`} />
        <StatTile tone="slate" label="Staff present" value={`${data.staff_present}/${data.staff}`} />
        <StatTile tone="slate" label="Transport" value={`${data.routes} route${data.routes === 1 ? '' : 's'}`} caption={`${data.sos_this_month} SOS this month · ${data.homework_this_month} homework set`} />
      </div>
      {body}
      {print && (
        <PrintSheet title="Director's report" onClose={() => setPrint(false)}>
          <PrintHeader schoolName={schoolName} docTitle="Director's Summary Report" right={<>As of {fmtDate(data.generated_at)}</>} />
          <table className="w-full text-sm border-collapse mb-4">
            <tbody>
              <tr><td>Total students</td><td className="text-right font-bold">{data.totals.strength} ({data.totals.boys} boys, {data.totals.girls} girls)</td></tr>
              <tr><td>Present today</td><td className="text-right font-bold">{data.totals.present_today} · month avg {data.attendance_month_percent ?? '—'}%</td></tr>
              <tr><td>Fees collected this month</td><td className="text-right font-bold">{inr(data.fees.collected_month)}</td></tr>
              <tr><td>Outstanding dues</td><td className="text-right font-bold">{inr(data.fees.outstanding)}</td></tr>
              <tr><td>Expenses this month</td><td className="text-right font-bold">{inr(data.expenses_month)}</td></tr>
              <tr><td>Admissions this month / open enquiries</td><td className="text-right font-bold">{data.admissions.admitted_this_month} / {data.admissions.open}</td></tr>
              <tr><td>Staff present today</td><td className="text-right font-bold">{data.staff_present} of {data.staff}</td></tr>
            </tbody>
          </table>
          {body}
        </PrintSheet>
      )}
    </>
  );
}

function DirectorBody({ data }) {
  return (
    <Card>
      <CardHeader title="Class-wise strength & today's attendance" />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Class</th><th className="px-4 py-2.5">Class teacher</th><th className="px-4 py-2.5 text-right">Boys</th><th className="px-4 py-2.5 text-right">Girls</th><th className="px-4 py-2.5 text-right">Total</th><th className="px-4 py-2.5 text-right">Present</th><th className="px-4 py-2.5 text-right">Absent</th></tr></thead>
          <tbody>
            {data.classes.map(c => (
              <tr key={c.id} className="border-t border-line-soft">
                <td className="px-4 py-2.5 font-semibold">{c.name}</td><td className="px-4 py-2.5">{c.class_teacher || '—'}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{c.boys}</td><td className="px-4 py-2.5 text-right tabular-nums">{c.girls}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-bold">{c.strength}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-success">{c.present_today}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-critical">{c.absent_today}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-line font-bold">
              <td className="px-4 py-2.5" colSpan={2}>Total</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{data.totals.boys}</td><td className="px-4 py-2.5 text-right tabular-nums">{data.totals.girls}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{data.totals.strength}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{data.totals.present_today}</td><td className="px-4 py-2.5 text-right tabular-nums">{data.totals.absent_today}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="px-5 py-2 text-xs text-ink-soft">Boys/girls counts use each student's gender field; students without one appear only in Total.</p>
    </Card>
  );
}

// ── UDISE enrolment ─────────────────────────────────────────
const CATS = ['General', 'OBC', 'SC', 'ST', 'EWS', 'Not set'];

export function UdiseReport({ schoolName }) {
  const [data, msg] = useLoad(() => getUdiseReport(), []);
  const [print, setPrint] = useState(false);
  if (msg) return <Msg msg={msg} />;
  if (!data) return <p className="text-sm text-ink-soft">Loading…</p>;

  // pivot: class -> category -> { boys, girls }
  const classes = [...new Set(data.rows.map(r => r.class_name))];
  const cell = (cls, cat) => data.rows.find(r => r.class_name === cls && r.category === cat);
  const table = (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className={theadRowCls}>
          <th className="px-3 py-2" rowSpan={2}>Class</th>
          {CATS.map(c => <th key={c} className="px-3 py-2 text-center" colSpan={2}>{c}</th>)}
          <th className="px-3 py-2 text-center" colSpan={3}>Total</th>
        </tr>
        <tr className={theadRowCls}>
          {CATS.map(c => [<th key={`${c}b`} className="px-2 py-1 text-center">B</th>, <th key={`${c}g`} className="px-2 py-1 text-center">G</th>])}
          <th className="px-2 py-1 text-center">B</th><th className="px-2 py-1 text-center">G</th><th className="px-2 py-1 text-center">All</th>
        </tr>
      </thead>
      <tbody>
        {classes.map(cls => {
          const rows = data.rows.filter(r => r.class_name === cls);
          const tot = rows.reduce((a, r) => ({ b: a.b + r.boys, g: a.g + r.girls, t: a.t + r.total }), { b: 0, g: 0, t: 0 });
          return (
            <tr key={cls} className="border-t border-line-soft">
              <td className="px-3 py-2 font-semibold">{cls}</td>
              {CATS.map(c => { const x = cell(cls, c); return [
                <td key={`${c}b`} className="px-2 py-2 text-center tabular-nums">{x?.boys || ''}</td>,
                <td key={`${c}g`} className="px-2 py-2 text-center tabular-nums">{x?.girls || ''}</td>,
              ]; })}
              <td className="px-2 py-2 text-center tabular-nums font-bold">{tot.b}</td>
              <td className="px-2 py-2 text-center tabular-nums font-bold">{tot.g}</td>
              <td className="px-2 py-2 text-center tabular-nums font-bold">{tot.t}</td>
            </tr>
          );
        })}
        {!classes.length && <EmptyRow cols={CATS.length * 2 + 4}>No students</EmptyRow>}
      </tbody>
    </table>
  );
  const m = data.missing;
  return (
    <>
      {(m.gender > 0 || m.category > 0) && (
        <p className="flex items-center gap-2 text-sm font-semibold text-caution bg-caution-tint px-3 py-2 rounded-s w-fit mb-3">
          <AlertTriangle size={15} /> Of {m.total} students, {m.gender} have no gender and {m.category} have no category set — fill these on the Students page for an accurate UDISE return.
        </p>
      )}
      <div className="flex justify-end gap-2 mb-3">
        <Button variant="secondary" className="flex items-center gap-1.5" onClick={() => downloadCsv('udise-enrolment.csv', [
          { key: 'class_name', label: 'Class' }, { key: 'category', label: 'Category' }, { key: 'boys', label: 'Boys' },
          { key: 'girls', label: 'Girls' }, { key: 'other', label: 'Other' }, { key: 'gender_not_set', label: 'Gender not set' }, { key: 'total', label: 'Total' },
        ], data.rows)}><Download size={14} /> CSV</Button>
        <Button variant="secondary" onClick={() => setPrint(true)} className="flex items-center gap-1.5"><Printer size={14} /> Print</Button>
      </div>
      <Card><div className="overflow-x-auto">{table}</div></Card>
      {print && (
        <PrintSheet title="UDISE enrolment" onClose={() => setPrint(false)}>
          <PrintHeader schoolName={schoolName} docTitle="Enrolment by Class, Gender & Social Category (UDISE+)" right={<>As of {fmtDate(todayISO())}</>} />
          {table}
        </PrintSheet>
      )}
    </>
  );
}
